import { Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClockSolidIcon16pxSvg from '../assets/images/icons/ClockSolidIcon16pxSvg';
import { StyledTextFieldTimePicker } from './StyledComponents';

const CustomTimePicker = (props) => {
   const { t } = useTranslation();
   const [open, setOpen] = useState(false);
   const [hours, setHours] = useState(0);
   const [minutes, setMinutes] = useState(0);
   const [seconds, setSeconds] = useState(0);

   const { value, onTimeChange, hoursLabel, secondsEnabled, readOnly, name, maxHours, className, disabled = false } = props;

   useEffect(() => {
      // const [initialHours, initialMinutes] = value.split(':').map(Number);
      const sanitizedValue = value || (secondsEnabled ? '00:00:00' : '00:00');
      const timeParts = sanitizedValue.split(':').map(Number);
      const [initialHours, initialMinutes, initialSeconds] = [
         timeParts[0] || 0, // Default to 0 if undefined
         timeParts[1] || 0, // Default to 0 if undefined
         timeParts[2] || 0, // If seconds are not provided, default to 0
      ];
      setHours(initialHours);
      setMinutes(initialMinutes);
      setSeconds(initialSeconds);
   }, [value]);

   const updateValue = (newHours, newMinutes, newSeconds) => {
      // const formattedValue = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      const formattedHours = newHours === 24 ? '24' : String(newHours).padStart(2, '0');
      const formattedMinutes = String(newMinutes).padStart(2, '0');
      const formattedSeconds = String(newSeconds).padStart(2, '0');
      const formattedValue = secondsEnabled
         ? `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
         : `${formattedHours}:${formattedMinutes}`;
      onTimeChange(name, formattedValue);
   };

   const [error, setError] = useState('');

   const handleHoursChange = (e) => {
      let newHours = Number(e.target.value);
      //before
      // setHours(newHours);
      // updateValue(newHours, minutes, seconds);

      //after
      if (newHours > maxHours) {
         // setError(`Hours cannot exceed ${maxHours}`);
         newHours = 0;
      }
      if (newHours === maxHours) {
         setMinutes(0);
         setSeconds(0);
         updateValue(newHours, 0, 0);
      } else {
         updateValue(newHours, minutes, seconds);
      }
      setHours(newHours);
   };

   const handleMinutesChange = (e) => {
      let newMinutes = Number(e.target.value);
      //before
      //setMinutes(newMinutes);
      //updateValue(hours, newMinutes, seconds);

      //after
      if (newMinutes > 59) {
         // setError(`Minutes cannot exceed 59`);
         newMinutes = 0;
      }
      if (hours >= maxHours) {
         setSeconds(0);
         setMinutes(0);
      } else {
         setMinutes(newMinutes);
         updateValue(hours, newMinutes, seconds);
      }
   };

   const handleSecondsChange = (e) => {
      let newSeconds = Number(e.target.value);
      //before
      //setSeconds(newSeconds);
      //updateValue(hours, minutes, newSeconds);
      if (newSeconds > 59) {
         // setError(`Seconds cannot exceed 59`);
         newSeconds = 0;
      }
      if (hours >= maxHours) {
         setSeconds(0);
         setMinutes(0);
      } else {
         setSeconds(newSeconds);
         updateValue(hours, minutes, newSeconds);
      }
   };

   const handleToggleOpen = () => {
      if (disabled) {
         return;
      }
      setOpen(!open);
   };

   const handleClose = () => {
      setOpen(false);
   };

   const timePickerSx = {
      '& .MuiOutlinedInput-root': {
         height: 40,
         backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
         '& fieldset': {
            borderColor: '#E5E7EB',
         },
         '&:hover fieldset': {
            borderColor: '#E5E7EB',
         },
         '&.Mui-focused fieldset': {
            borderColor: '#E5E7EB',
         },
      },
      '& .MuiOutlinedInput-input': {
         height: '40px',
         padding: '0 12px',
         boxSizing: 'border-box',
         lineHeight: '40px',
         backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
         WebkitTextFillColor: disabled ? '#6B7280' : '#6B7280',
      },
      '& .MuiOutlinedInput-notchedOutline': {
         borderColor: '#E5E7EB',
      },
      '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
         borderColor: '#E5E7EB !important',
      },
      '& .MuiButtonBase-root': {
         padding: '13px 14px',
         backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
         border: 'none',
         borderRadius: '0px 6px 6px 0px',
         '&:hover': {
            backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
         },
         '&.Mui-disabled': {
            backgroundColor: '#F3F4F6',
            color: '#9CA3AF',
         },
      },
   };

   return (
      <div>
         <StyledTextFieldTimePicker
            name={name}
            className={className}
            disabled={disabled}
            value={value}
            variant='outlined'
            size='small'
            focused={false}
            sx={timePickerSx}
            InputProps={{
               readOnly: true,
               endAdornment: (
                  <InputAdornment position='end'>
                     <div className='flex items-center'>
                        {hoursLabel && (
                           <div
                              className='px-3 py-3 border-l'
                              style={{
                                 backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
                                 borderColor: '#E5E7EB',
                              }}
                           >
                              <p className='text-[#6B7280]'>{t('common.hours')}</p>
                           </div>
                        )}
                        <IconButton className='h-10' onClick={handleToggleOpen} disableRipple disabled={disabled}>
                           <ClockSolidIcon16pxSvg />
                        </IconButton>
                     </div>
                  </InputAdornment>
               ),
            }}
         />
         <Dialog open={open} onClose={handleClose}>
            <DialogTitle>{t('common.select-time')}</DialogTitle>
            <DialogContent>
               <div className='flex space-x-2 items-center pt-2'>
                  <div>
                     {/* <p className='text-center'>Hours</p> */}
                     <TextField
                        className='w-28'
                        label={t('common.hours')}
                        type='number'
                        // value={hours}
                        value={String(hours).padStart(2, '0')}
                        onChange={handleHoursChange}
                        InputProps={{ inputProps: { min: 0 } }}
                        
                     />
                  </div>

                  <div>
                     {/* <p className='text-center'>Minutes</p> */}
                     <div className='flex items-center gap-2'>
                        <p>:</p>
                        <TextField
                           className='w-28'
                           label={t('common.minutes')}
                           type='number'
                           // value={minutes}
                           value={String(minutes).padStart(2, '0')}
                           onChange={handleMinutesChange}
                           InputProps={{ inputProps: { min: 0 } }}
                        />
                     </div>
                  </div>
                  {secondsEnabled === true && (
                     <div>
                        {/* <p className='text-center text-xs'>Seconds</p> */}
                        <div className='flex items-center gap-2'>
                           <p>:</p>
                           <TextField
                              className='w-28'
                              label='Seconds'
                              type='number'
                              // value={seconds}
                              value={String(seconds).padStart(2, '0')}
                              onChange={handleSecondsChange}
                              InputProps={{ inputProps: { min: 0 } }}
                           />
                        </div>
                     </div>
                  )}
               </div>
            </DialogContent>
         </Dialog>
      </div>
   );
};

export default CustomTimePicker;
